import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { CalendarDays, Check, Copy, ExternalLink, Undo2, UserX } from "lucide-react";
import Button from "shared/Button";
import StatusPill from "shared/StatusPill";
import Alert from "shared/Alert";
import Modal from "shared/Modal";
import ConfirmModal from "shared/ConfirmModal";
import { showInfo, showSuccess } from "shared/toast";
import { FORM_CONTROL } from "shared/tableStyles";
import { apiErrorMessage } from "../apiError";
import { getPrimeCompany, updatePrimeContact, type PrimeContactWithAppearanceCount } from "../api/CrmApi";
import { KIND_LABELS, daysAgo, formatCalendarDate, formatKarachiClock, karachiDateOf, karachiToday } from "../outreachDates";
import { postTouchAction, type ConversationOutcome, type TouchAction, type TouchView } from "../api/OutreachTouchesApi";
import { announceDueChange } from "../modal/useDueCounts";
import ReceivedAtField, { receivedAtIso } from "./ReceivedAtField";

const ACTION_LABELS: Record<TouchAction, string> = {
  sent: "Sent", accepted: "Accepted", replied: "Replied", no_profile: "Profile not found", close: "Close (not accepted)",
  undo: "Undo", reopen: "Reopen", set_contact: "Set contact",
};

const DANGER_OUTLINE = "border-destructive/50 text-destructive hover:bg-destructive/10";

function crmBase() {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/crm" : "/employee/crm";
}

interface Props {
  touch: TouchView;
  /** Called with the server's warnings after any successful action; the page reloads its lists. */
  onChanged: (touchId: number) => void;
  /** Read-only rendering (the timeline reuses the same card without buttons). */
  readOnly?: boolean;
  /** Due-list rows: the draft shows as one line and the "happened on" date picker stays hidden until asked for. */
  compact?: boolean;
  /** The RFP is named by a heading above the card, so the card leaves its link out. */
  hideRfp?: boolean;
  /** Bulk selection (Needs attention): a checkbox in front of the name. */
  selectable?: { checked: boolean; onToggle: () => void };
}

/** What this touch is waiting on, in words (the "needs" group has nothing to copy). */
function missingNote(t: TouchView): string | null {
  if (t.needs_person) return "No named person yet: pick one of the company's contacts (Set contact), then the drafts use their first name.";
  if (t.kind === "connection_request" && t.needs_profile) return "This contact has no LinkedIn profile URL yet: add it on their page, or use Mark Profile not found.";
  return null;
}

export default function TouchCard({ touch, onChanged, readOnly, compact, hideRfp, selectable }: Props) {
  const [showDraft, setShowDraft] = useState(!compact);
  const [busy, setBusy] = useState(false);
  const [dateFor, setDateFor] = useState<null | "sent" | "accepted">(null);
  const [pickedDate, setPickedDate] = useState("");
  const [popoverPos, setPopoverPos] = useState<{ top?: number; bottom?: number; left: number } | null>(null);
  const splitRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const popoverRef = useRef<HTMLFormElement>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState<null | "replied" | "no_profile" | "close" | "set_contact">(null);
  const [text, setText] = useState("");
  const [outcome, setOutcome] = useState<ConversationOutcome | "">("");
  const [receivedAt, setReceivedAt] = useState("");
  const [profileInput, setProfileInput] = useState("");
  const [people, setPeople] = useState<PrimeContactWithAppearanceCount[] | null>(null);
  const [pickedId, setPickedId] = useState<number | null>(null);
  const draftRef = useRef<HTMLPreElement>(null);

  async function run(action: TouchAction, extra: { note?: string; reason?: string; prime_contact_id?: number; on?: string; outcome?: ConversationOutcome; received_at?: string } = {}) {
    setBusy(true);
    setError("");
    try {
      const result = await postTouchAction(touch.opportunity_id, touch.id, { action, ...extra });
      setWarnings(result.warnings);
      result.warnings.forEach((w) => showInfo(w));
      if (result.warnings.length === 0) showSuccess(`${ACTION_LABELS[action]} recorded`);
      setDialog(null);
      setDateFor(null);   // the popover must not linger while the row slides away
      setText("");
      announceDueChange();
      onChanged(touch.id);
    } catch (err: any) {
      setError(apiErrorMessage(err, "Could not record that. Reload and try again."));
    } finally {
      setBusy(false);
    }
  }

  async function copyDraft() {
    const draft = touch.text ?? "";
    try {
      await navigator.clipboard.writeText(draft);
      showSuccess("Copied");
    } catch {
      // clipboard unavailable (insecure context, blocked): select the text so Ctrl+C works
      setShowDraft(true);
      setTimeout(() => {
        const node = draftRef.current;
        if (node) {
          const range = document.createRange();
          range.selectNodeContents(node);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
      }, 0);
      showInfo("Could not copy automatically. The text is selected: press Ctrl+C.");
    }
  }

  async function saveProfile() {
    const url = profileInput.trim();
    if (!touch.prime_contact_id) return;
    if (!/^https?:\/\/([a-z0-9-]+\.)*linkedin\.com\/in\//i.test(url)) {
      setError("Paste the person's own LinkedIn profile link (it looks like https://www.linkedin.com/in/name).");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await updatePrimeContact(touch.prime_contact_id, { personal_linkedin_url: url });
      showSuccess("Profile saved");
      setProfileInput("");
      announceDueChange();
      onChanged(touch.id);
    } catch (err: any) {
      setError(apiErrorMessage(err, "Could not save the profile link."));
    } finally {
      setBusy(false);
    }
  }

  async function openSetContact() {
    setDialog("set_contact");
    if (people === null) {
      try {
        const company = await getPrimeCompany(touch.prime_company_id);
        setPeople((company.prime_contacts ?? []).filter((p) => (p.first_name ?? "").trim() && p.status === "active"));
      } catch {
        setPeople([]);
      }
    }
  }

  const missing = missingNote(touch);
  function toggleDatePicker(action: "sent" | "accepted") {
    if (dateFor === action) { setDateFor(null); return; }
    const rect = splitRefs.current[action]?.getBoundingClientRect();
    if (rect) {
      // keep the 256px popover on screen: clamp to the right edge, and open upwards when there is no room below
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - 272));
      setPopoverPos(rect.bottom + 8 + 150 > window.innerHeight ? { bottom: window.innerHeight - rect.top + 8, left } : { top: rect.bottom + 8, left });
    }
    setPickedDate("");
    setDateFor(action);
  }

  // Move focus into the popover when it opens so the keyboard can pick the day and press Enter.
  useEffect(() => {
    if (dateFor) document.getElementById(`on-${touch.id}`)?.focus();
  }, [dateFor, touch.id]);

  // The date popover is portalled (the rows clip their overflow) so it closes on outside click, Escape, scroll and resize.
  useEffect(() => {
    if (!dateFor) return;
    const close = () => setDateFor(null);
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || splitRefs.current[dateFor]?.contains(target)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      close();
      splitRefs.current[dateFor]?.querySelector<HTMLButtonElement>("button[aria-expanded]")?.focus();   // keyboard users land back on the button
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [dateFor]);

  // Needs-attention cards (no profile, no person, campaign not started) have not reached anyone, so there can be no reply.
  // A card that has a profile link asking to skip is reporting a dead/wrong link, not a missing profile.
  const deadLink = !!touch.profile_url && !touch.needs_profile;
  const notContacted = touch.state === "planned" && ((touch.kind === "connection_request" && touch.needs_profile) || touch.needs_person || touch.display === "waiting_start");
  const sendAfterNote = touch.kind === "acceptance_message" && touch.state === "planned" && touch.send_after
    ? `Accepted: send after ${formatKarachiClock(touch.send_after)} (3 hours after the acceptance)${touch.due_on && touch.due_on > karachiDateOf(touch.send_after) ? `, which is the next business day: ${formatCalendarDate(touch.due_on)}` : ""}.`
    : null;
  const isLateAcceptance = touch.kind === "connection_request" && touch.state === "stopped" && touch.stored_state === "done";
  const dueLabel = touch.due_on ? formatCalendarDate(touch.due_on) : "";
  const limit = touch.kind === "connection_request" ? `${touch.chars} / 200 characters` : `${touch.words} words`;

  /** "Mark as sent" for today, with a joined calendar button to record it for another day instead. */
  function renderMark(action: "sent" | "accepted", label: string) {
    return (
      <div className="inline-flex" ref={(el) => { splitRefs.current[action] = el; }}>
        <Button variant="primary" size="sm" className="rounded-r-none" disabled={busy} onClick={() => run(action)}>
          <Check className="w-3.5 h-3.5" /> {label}
        </Button>
        <Button
          variant="primary"
          size="sm"
          className="rounded-l-none border-l border-primary-foreground/30 px-2"
          disabled={busy}
          aria-label={`${label} on a different day`}
          aria-expanded={dateFor === action}
          title="Record it for a different day"
          onClick={() => toggleDatePicker(action)}
        >
          <CalendarDays className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-4 space-y-3 shadow-sm" data-testid={`touch-${touch.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-foreground">
            {selectable && <input type="checkbox" checked={selectable.checked} onChange={selectable.onToggle} aria-label={`Select ${touch.company_name ?? "company"}`} />}
            {touch.company_name ?? "Company"}
            <StatusPill tone="info" size="md">{KIND_LABELS[touch.kind]}</StatusPill>
            {!hideRfp && (
              <>
                <span className="font-normal text-muted-foreground">·</span>
                <Link className="font-normal text-muted-foreground underline underline-offset-2" to={`${crmBase()}?opportunity_id=${touch.opportunity_id}`}>{touch.rfp_title ?? "RFP"}</Link>
              </>
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            {touch.contact_name && <span className="text-sm text-foreground">{touch.contact_name}</span>}
            {[touch.contact_title, touch.contact_email].filter(Boolean).map((v) => <span key={v}>{" · "}{v}</span>)}
            {touch.prime_contact_id && (
              <>
                {" · "}
                <Link className="underline underline-offset-2" to={`${crmBase()}/prime-contacts/${touch.prime_contact_id}`}>contact page</Link>
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Only where the profile is missing (the Needs attention stage): a problem, not a next step, so danger-coloured and first. */}
          {!readOnly && touch.needs_profile && touch.actions.includes("no_profile") && (
            <Button variant="outline" size="sm" className={DANGER_OUTLINE} disabled={busy} onClick={() => { setText(""); setDialog("no_profile"); }}>
              <UserX className="w-3.5 h-3.5" /> Mark Profile not found
            </Button>
          )}
          {touch.overdue_days > 0 && <StatusPill tone="warning" size="sm">overdue by {touch.overdue_days} day{touch.overdue_days === 1 ? "" : "s"}</StatusPill>}
          {touch.display === "awaiting_acceptance" && touch.close_on && <StatusPill tone="info" size="sm">closes {formatCalendarDate(touch.close_on)}</StatusPill>}
          {dueLabel && touch.state === "planned" && <StatusPill tone="neutral" size="sm">due {dueLabel}</StatusPill>}
          {touch.state === "done" && touch.sent_on && <StatusPill tone="success" size="sm">sent {formatCalendarDate(touch.sent_on)}{daysAgo(touch.sent_on) && ` · ${daysAgo(touch.sent_on)}`}</StatusPill>}
          {touch.accepted_on && <StatusPill tone="success" size="sm">accepted {formatCalendarDate(touch.accepted_on)}{daysAgo(touch.accepted_on) && ` · ${daysAgo(touch.accepted_on)}`}</StatusPill>}
          {(touch.state === "stopped" || touch.state === "skipped") && <StatusPill tone="neutral" size="sm">{touch.state}</StatusPill>}
        </div>
      </div>

      {touch.profile_url && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <a href={touch.profile_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline underline-offset-2">
            <ExternalLink className="w-3 h-3" /> LinkedIn profile
          </a>
          {/* A link that is dead or points to the wrong person: same skip as "profile not found", with the reason marked as a dead link. */}
          {!readOnly && !touch.needs_profile && touch.actions.includes("no_profile") && (
            <button type="button" disabled={busy} className="text-xs text-destructive underline underline-offset-2 disabled:opacity-50" onClick={() => { setText(""); setDialog("no_profile"); }}>
              Dead link?
            </button>
          )}
        </div>
      )}
      {touch.reason && <p className="text-xs text-muted-foreground">{touch.reason}</p>}
      {sendAfterNote && <Alert tone="info">{sendAfterNote}</Alert>}
      {touch.blocked && <Alert tone="danger">{touch.blocked}: nothing is sent to this person.</Alert>}
      {touch.primary_contact_now && (
        <Alert tone="info">The company's primary contact is now {touch.primary_contact_now.name}; this path stays with {touch.contact_name}.</Alert>
      )}
      {missing && <Alert tone="warning">{missing}</Alert>}
      {!readOnly && touch.kind === "connection_request" && touch.needs_profile && touch.prime_contact_id && (
        <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (!busy && profileInput.trim()) void saveProfile(); }}>
          <input type="url" aria-label="LinkedIn profile link" value={profileInput} onChange={(e) => setProfileInput(e.target.value)} placeholder="Paste their LinkedIn profile link"
            className={`flex-1 min-w-[16rem] px-3 py-1.5 ${FORM_CONTROL}`} />
          <Button type="submit" variant="primary" size="sm" disabled={busy || !profileInput.trim()}>Save profile</Button>
        </form>
      )}
      {isLateAcceptance && <Alert tone="warning">This request closed unaccepted. If the person did accept, Accepted still records it, and the message dates are recalculated from that day.</Alert>}

      {touch.text && !missing && (
        <div className="rounded-lg border border-border bg-surface-muted p-3">
          {showDraft
            ? <pre ref={draftRef} className="whitespace-pre-wrap break-words text-sm text-foreground font-inter" data-testid="draft-text">{touch.text}</pre>
            : <p className="truncate text-sm text-foreground font-inter" data-testid="draft-preview">{touch.text}</p>}
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {limit}
              {compact && (
                <button type="button" className="ml-2 underline underline-offset-2" aria-expanded={showDraft} onClick={() => setShowDraft((v) => !v)}>
                  {showDraft ? "Hide full text" : "Show full text"}
                </button>
              )}
            </span>
            {!readOnly && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={copyDraft}><Copy className="w-3.5 h-3.5" /> Copy</Button>
              </div>
            )}
          </div>
        </div>
      )}
      {touch.text && missing && touch.needs_person && (
        <pre className="whitespace-pre-wrap break-words text-xs text-muted-foreground rounded-lg border border-dashed border-border p-2">{touch.text}</pre>
      )}

      {error && <Alert tone="danger" role="alert">{error}</Alert>}
      {warnings.length > 0 && warnings.map((w) => <Alert key={w} tone="warning">{w}</Alert>)}

      {!readOnly && touch.actions.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">
          <div className="flex flex-wrap items-center gap-2">
            {touch.actions.includes("sent") && renderMark("sent", "Mark as sent")}
            {touch.actions.includes("accepted") && renderMark("accepted", "Mark as accepted")}
            {touch.actions.includes("set_contact") && <Button variant="brand-outline" size="sm" disabled={busy} onClick={openSetContact}>Set contact</Button>}
            {touch.actions.includes("reopen") && <Button variant="brand-outline" size="sm" disabled={busy} onClick={() => run("reopen")}>Reopen</Button>}
            {touch.actions.includes("replied") && !notContacted && <Button variant="outline" size="sm" disabled={busy} onClick={() => { setText(""); setOutcome(""); setReceivedAt(""); setDialog("replied"); }}>Mark as replied</Button>}
            {touch.actions.includes("undo") && <Button variant="ghost" size="sm" disabled={busy} onClick={() => run("undo")}><Undo2 className="w-3.5 h-3.5" /> Undo</Button>}
          </div>
          {/* Problems: kept apart from the normal next step and in the danger colour so they are never pressed by accident. */}
          {touch.actions.includes("close") && (
            <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
              {touch.actions.includes("close") && <Button variant="outline" size="sm" className={DANGER_OUTLINE} disabled={busy} onClick={() => setDialog("close")}>Close (not accepted)</Button>}
            </div>
          )}
        </div>
      )}

      {dateFor && popoverPos && createPortal(
        <form
          ref={popoverRef}
          onSubmit={(e) => { e.preventDefault(); if (pickedDate && !busy) run(dateFor, { on: pickedDate }); }}
          role="dialog"
          aria-label="Choose the day"
          style={{ position: "fixed", top: popoverPos.top, bottom: popoverPos.bottom, left: popoverPos.left }}
          className="z-50 w-64 rounded-xl border border-border bg-surface p-3 shadow-lg space-y-2"
        >
          <label className="block text-xs font-medium text-muted-foreground" htmlFor={`on-${touch.id}`}>Happened on</label>
          <input id={`on-${touch.id}`} type="date" value={pickedDate} onChange={(e) => setPickedDate(e.target.value)} max={karachiToday()} className={`w-full px-2 py-1.5 ${FORM_CONTROL}`} />
          <Button type="submit" variant="primary" size="sm" className="w-full" disabled={busy || !pickedDate}>
            {dateFor === "sent" ? "Mark as sent on this date" : "Mark as accepted on this date"}
          </Button>
        </form>,
        document.body,
      )}

      {dialog === "replied" && (
        <ConfirmModal
          title="Mark as replied?"
          confirmLabel="Yes, they replied"
          confirming={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => run("replied", { ...(text.trim() ? { note: text.trim() } : {}), ...(outcome ? { outcome } : {}), ...(receivedAtIso(receivedAt) ? { received_at: receivedAtIso(receivedAt) } : {}) })}
          message={
            <div className="space-y-2">
              <p>This stops the whole LinkedIn path for {touch.company_name} on this RFP (nothing more is planned for them) and logs the reply as a conversation. You can undo it today.</p>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={2000} placeholder="What they said (optional)" className={`w-full px-3 py-2 ${FORM_CONTROL}`} />
              <ReceivedAtField id={`reply-at-${touch.id}`} value={receivedAt} onChange={setReceivedAt} disabled={busy} />
              <label htmlFor={`reply-outcome-${touch.id}`} className="block text-xs font-medium text-muted-foreground">Where it stands</label>
              <select id={`reply-outcome-${touch.id}`} value={outcome} onChange={(e) => setOutcome(e.target.value as ConversationOutcome | "")} className={`w-full px-3 py-2 ${FORM_CONTROL}`}>
                <option value="">Not sure yet</option>
                <option value="interested">Interested</option>
                <option value="other">Other</option>
              </select>
              <p className="text-xs text-muted-foreground">The reply starts a conversation under Conversations, where you can keep the thread, close it or pass it to a colleague.</p>
            </div>
          }
        />
      )}
      {dialog === "close" && (
        <ConfirmModal
          title="Close this request?"
          confirmLabel="Close it"
          confirming={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => run("close")}
          message="The request was not accepted. The message and follow-up for this person are stopped. You can undo this today."
        />
      )}
      {dialog === "no_profile" && (
        <Modal size="md" title={deadLink ? "Dead or wrong profile link" : "Profile not found"} description="Skips this company's whole LinkedIn path" onClick={() => setDialog(null)} closeButtonCLick={() => setDialog(null)}
          footer={<><Button variant="outline" size="sm" onClick={() => setDialog(null)}>Cancel</Button><Button variant="primary" size="sm" disabled={busy || !text.trim()} onClick={() => run("no_profile", { reason: deadLink ? `Dead link: ${text.trim()}` : text.trim() })}>Skip this path</Button></>}>
          <label className="text-xs text-muted-foreground block mb-1" htmlFor={`np-${touch.id}`}>{deadLink ? "What is wrong with the link (required)" : "Where you looked and what you found (required)"}</label>
          <textarea id={`np-${touch.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={deadLink ? 285 : 300} className={`w-full px-3 py-2 ${FORM_CONTROL}`} />
        </Modal>
      )}
      {dialog === "set_contact" && (
        <Modal size="md" title="Set contact" description="Pick the person this path goes to" onClick={() => setDialog(null)} closeButtonCLick={() => setDialog(null)}
          footer={<><Button variant="outline" size="sm" onClick={() => setDialog(null)}>Cancel</Button><Button variant="primary" size="sm" disabled={busy || pickedId === null} onClick={() => pickedId !== null && run("set_contact", { prime_contact_id: pickedId })}>Use this person</Button></>}>
          {people === null ? <p className="text-sm text-muted-foreground">Loading…</p> : people.length === 0 ? (
            <p className="text-sm text-muted-foreground">This company has no active contact with a first name. Add one on the company page first.</p>
          ) : (
            <select value={pickedId ?? ""} onChange={(e) => setPickedId(e.target.value ? Number(e.target.value) : null)} className={`w-full px-3 py-2 ${FORM_CONTROL}`} aria-label="Contact">
              <option value="">Choose a person…</option>
              {people.map((p) => <option key={p.id} value={p.id}>{[p.first_name, p.last_name].filter(Boolean).join(" ")}{p.title ? ` · ${p.title}` : ""} ({p.primary_email})</option>)}
            </select>
          )}
          <p className="text-xs text-muted-foreground mt-2">The drafts will say their first name; read each draft once before sending.</p>
        </Modal>
      )}
    </div>
  );
}
